import React, { useCallback, useMemo, useState } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import Tooltip from 'components_new/atoms/Tooltip'

import Node, { InputType } from 'components_new/molecules/Node'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import { BOARD } from 'components_new/molecules/Node/accents'

import NodeType from 'types/transformation/enums/NodeType'
import {
  MapNode,
  Node as TNode
} from 'types/transformation/types/nodes'
import { SimTable } from 'types/transformation/types/tables'
import {
  DEPRECATED_GROUP,
  FIELD_SECTIONS
} from 'types/transformation/types/sections'

import { DATA_TYPES } from 'helpers/Constants'

import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from './hooks'
import { groupBySection, IndexedField } from './mapSections'

interface MapNodeProps {
  id: string
  data: MapNode
  selected: boolean
  table: SimTable
}

const DEPRECATED_KEY = 'deprecated'

const MapOutput = (props: MapNodeProps) => {
  const { id, data, selected, table } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<MapNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      schema: nodeData.schema.map((col) => ({
        ...col,
        left_column: pruneRef(col.left_column, inputSchema)
      }))
    }),
    [inputSchema]
  )

  const handleSelected = useCallback(
    (columnIndex: number, leftColumn: string | null) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as MapNode

          newNodeData.schema[columnIndex].left_column = leftColumn

          return { ...node, data: newNodeData }
        })
      )
    },
    [setNodes, id]
  )

  const grouped = useMemo(() => groupBySection(data.schema), [data.schema])

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const toggle = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))

  const descriptionByKey = useMemo(() => {
    const map: Record<string, string> = {
      [DEPRECATED_KEY]: DEPRECATED_GROUP.description
    }

    FIELD_SECTIONS.forEach((definition) => {
      map[definition.key] = definition.description
    })

    return map
  }, [])

  const renderRow = ({ field, index }: IndexedField, dimmed: boolean) => (
    <Box
      key={field.id}
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1,
        opacity: dimmed ? 0.5 : 1
      }}
    >
      <Box sx={{ flex: 1.15, flexShrink: 0, minWidth: 0 }}>
        <FieldSelect
          clearable
          onClear={() => handleSelected(index, null)}
          placeholder="Ej mappad"
          value={field.left_column}
          options={fieldOptions}
          onChange={(value) => handleSelected(index, value)}
        />
      </Box>
      <Icon
        name="ArrowForward"
        sx={{ color: '#C4C4CC', flex: 'none', fontSize: '15px' }}
      />
      <Tooltip title={field.description ?? ''}>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minWidth: 0
          }}
        >
          <Box
            sx={{
              color: BOARD.textPrimary,
              fontFamily: BOARD.fontBody,
              fontSize: '12.5px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {field.name}
          </Box>
          <Box
            sx={{
              color: BOARD.textFaint,
              fontFamily: BOARD.fontBody,
              fontSize: '9.5px'
            }}
          >
            {DATA_TYPES[field.type]}
          </Box>
        </Box>
      </Tooltip>
    </Box>
  )

  const renderSection = (
    key: string,
    label: string,
    fields: IndexedField[],
    dimmed: boolean
  ) => (
    <Box key={key}>
      <Tooltip title={descriptionByKey[key] ?? ''}>
        <Box
          onClick={() => toggle(key)}
          sx={{
            alignItems: 'center',
            cursor: 'pointer',
            display: 'flex',
            gap: '7px',
            pb: 0.25,
            pt: 1
          }}
        >
          <Icon
            name={collapsed[key] ? 'ChevronRight' : 'ExpandMore'}
            sx={{ color: BOARD.textMuted, fontSize: '15px' }}
          />
          <Box
            sx={{
              color: dimmed ? BOARD.textFaint : BOARD.textPrimary,
              fontFamily: BOARD.fontDisplay,
              fontSize: '12px',
              fontWeight: 600
            }}
          >
            {label}
          </Box>
          <Box
            sx={{
              backgroundColor: BOARD.chipBackground,
              borderRadius: '20px',
              color: BOARD.textFaint,
              fontFamily: BOARD.fontBody,
              fontSize: '10.5px',
              fontWeight: 600,
              px: '7px',
              py: '1px'
            }}
          >
            {fields.length}
          </Box>
        </Box>
      </Tooltip>
      <Collapse in={!collapsed[key]}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            pt: 0.5
          }}
        >
          {fields.map((item) => renderRow(item, dimmed))}
        </Box>
      </Collapse>
    </Box>
  )

  return (
    <Node
      error={isInvalid}
      icon="LogoutOutlined"
      inputType={InputType.SINGLE}
      title={'Output'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.MAP}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: 460 }}>
        {/* Flikrad: display name + technical name */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: 1,
            pb: 0.75
          }}
        >
          <Box
            sx={{
              color: BOARD.textPrimary,
              fontFamily: BOARD.fontDisplay,
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            {table.name}
          </Box>
          <Box
            sx={{
              backgroundColor: BOARD.chipBackground,
              borderRadius: '6px',
              color: BOARD.textMuted,
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: '10.5px',
              px: '7px',
              py: '2px'
            }}
          >
            {table.technical_name}
          </Box>
        </Box>

        {/* Field list — node grows to fit; no internal scroll */}
        <Box>
          {grouped.sections.map((section) => {
            const definition = FIELD_SECTIONS.find(
              (item) => item.key === section.key
            )

            return renderSection(
              section.key,
              definition?.label ?? section.key,
              section.fields,
              false
            )
          })}

          {grouped.deprecated.length > 0
            ? renderSection(
              DEPRECATED_KEY,
              DEPRECATED_GROUP.label,
              grouped.deprecated,
              true
            )
            : null}
        </Box>
      </Box>
    </Node>
  )
}

export default MapOutput
